import { useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
    AccountExclusion,
    PickBlock,
    PreviewChannel,
    PushNotificationEntry,
    RetentionPreview,
    RowStatus,
    fetchRetentionPreview,
} from "../hooks/usePushNotifications";

interface RetentionPreviewDialogProps {
    notifications: PushNotificationEntry[];
    onClose: () => void;
}

const exclusionLabels: Record<AccountExclusion, string> = {
    noProfile: "No user profile",
    demo: "Demo account",
    coach: "Coach account",
    organisationStaff: "Organisation staff",
    employee: "Employee (add to testUserIds to include)",
};

const blockLabels: Record<PickBlock, string> = {
    pausedAfterInactivity: "Paused: row 11 was sent and the user hasn't opened the app since",
    dailyLimit: "A retention message was already delivered today",
    weeklyLimit: "Weekly limit reached",
    alreadyPickedToday: "Today's message was already picked",
};

const statusStyles: Record<RowStatus, string> = {
    picked: "bg-green-100 text-green-800",
    lowerPriority: "bg-yellow-100 text-yellow-800",
    skipped: "bg-yellow-100 text-yellow-800",
    blocked: "bg-orange-100 text-orange-800",
    notMet: "bg-gray-100 text-gray-600",
    off: "bg-gray-100 text-gray-400",
};

const rowStatusText = (d: PreviewChannel["rows"][number]) => {
    switch (d.status) {
        case "picked": return "Picked";
        case "lowerPriority": return `Lower priority than row ${d.because}`;
        case "skipped": return `Skipped: row ${d.because} applies`;
        case "blocked": return "Condition met, blocked today";
        case "notMet": return "Condition not met";
        case "off": return d.conditionMet ? "Off (condition met)" : "Off";
    }
};

const RetentionPreviewDialog: React.FC<RetentionPreviewDialogProps> = ({ notifications, onClose }) => {
    const [user, setUser] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [preview, setPreview] = useState<RetentionPreview | null>(null);

    const rowName = (row: string) => notifications.find((n) => n.row === row)?.name ?? "";
    const rowLabel = (row: string) => `${row}. ${rowName(row)}`;

    const runPreview = async () => {
        if (!user.trim()) return;
        setLoading(true);
        setError(null);
        try {
            setPreview(await fetchRetentionPreview(user.trim()));
        } catch (err) {
            console.error("[RETENTION_PREVIEW] Error:", err);
            setPreview(null);
            setError((err as Error).message);
        }
        setLoading(false);
    };

    const channelSummary = (label: string, channel: PreviewChannel | undefined, extra?: string | null) => (
        <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-xs font-medium text-gray-500 uppercase mb-1">{label}</p>
            {!channel ? (
                <p className="text-sm text-gray-500">Can't be worked out</p>
            ) : channel.picked ? (
                <>
                    <p className="text-sm font-semibold">{rowLabel(channel.picked)}</p>
                    {channel.message && (
                        <p className="text-sm mt-1">
                            {channel.message.title} — {channel.message.body}
                            {channel.message.buttons.length > 0 && (
                                <span className="text-gray-500"> [{channel.message.buttons.join("] [")}]</span>
                            )}
                        </p>
                    )}
                    {extra && <p className="text-xs text-orange-700 mt-1">{extra}</p>}
                </>
            ) : (
                <p className="text-sm text-gray-600">
                    Nothing{channel.blockedBy ? `: ${blockLabels[channel.blockedBy]}` : ": no switched-on row's condition holds"}
                </p>
            )}
        </div>
    );

    const pushExtra = preview?.push?.pushBlockedBy === "noDevice"
        ? "No device with notifications on: no push, the banner waits for the next open"
        : preview?.push?.pushBlockedBy === "switchedOff"
            ? "The user switched this notification type off: no push, the banner waits for the next open"
            : null;

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black bg-opacity-50">
            <div className="bg-white rounded-t-lg sm:rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center px-4 sm:px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
                    <h2 className="text-lg font-heading">Preview for a user</h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-4 sm:px-6 py-4 space-y-4">
                    <p className="text-xs text-gray-500">
                        Which retention message the user would get today, and why. Nothing is sent or saved.
                    </p>
                    <form
                        className="flex gap-2"
                        onSubmit={(e) => { e.preventDefault(); runPreview(); }}
                    >
                        <input
                            type="text"
                            value={user}
                            onChange={(e) => setUser(e.target.value)}
                            placeholder="User ID or email"
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                        />
                        <button
                            type="submit"
                            disabled={loading || !user.trim()}
                            className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-800 text-white rounded-lg disabled:opacity-50"
                        >
                            {loading ? "Checking..." : "Preview"}
                        </button>
                    </form>

                    {error && <p className="text-sm text-red-500">{error}</p>}

                    {preview && (
                        <div className="space-y-4">
                            <div className="flex flex-wrap gap-2 text-xs">
                                <span className={`px-2 py-0.5 rounded-full ${preview.receives ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                                    {preview.receives ? "Gets retention notifications" : `Excluded: ${preview.exclusion ? exclusionLabels[preview.exclusion] : ""}`}
                                </span>
                                {preview.testAccount && <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">Test account</span>}
                                <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Mode: {preview.mode}</span>
                                {!preview.summaryStored && (
                                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Summary not built yet (shown as it would be built)</span>
                                )}
                            </div>
                            <p className="text-xs text-gray-500 break-all">User ID: {preview.uid}</p>
                            {!preview.receives && (
                                <p className="text-sm text-orange-700">This account gets nothing. The picks below show what it would get if it weren't excluded.</p>
                            )}

                            {!preview.timezone ? (
                                <p className="text-sm text-orange-700">
                                    No timezone yet: the user hasn't opened an app version that reports it, and has no daily reminder. Nothing is picked until they do.
                                </p>
                            ) : (
                                <>
                                    <p className="text-sm">
                                        Local time: {preview.localHour}:00 on {preview.localDate} ({preview.timezone})
                                        {preview.todaysRow && <> · Today's message: <strong>{rowLabel(preview.todaysRow)}</strong></>}
                                    </p>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {channelSummary("If they open the app before 12pm (banner)", preview.banner)}
                                        {channelSummary("At 12pm if they haven't (push)", preview.push, pushExtra)}
                                    </div>

                                    {preview.limits && (
                                        <p className="text-sm">
                                            Sent today: {preview.limits.sentToday ? "yes" : "no"} · Last 7 days: {preview.limits.sentThisWeek} / {preview.limits.weeklyLimit}
                                            {preview.limits.pausedAfterInactivity && " · Paused after row 11"}
                                        </p>
                                    )}

                                    {preview.currentBanners && preview.currentBanners.length > 0 && (
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Banners on the dashboard now</label>
                                            <ul className="text-sm list-disc pl-5 space-y-1">
                                                {preview.currentBanners.map((b) => (
                                                    <li key={b.slot}>
                                                        {rowLabel(b.row)} (from {b.day}{b.delivered ? "" : ", not seen yet"}){b.goalMet && " — goal met, clears on next open"}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {preview.push && (
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Every daily row (12pm push)</label>
                                            <div className="border rounded-lg divide-y">
                                                {preview.push.rows.map((d) => (
                                                    <div key={d.row} className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm">
                                                        <span className="text-gray-700">{rowLabel(d.row)}</span>
                                                        <span className={`px-2 py-0.5 text-[10px] uppercase rounded-full whitespace-nowrap ${statusStyles[d.status]}`}>
                                                            {rowStatusText(d)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                                The banner uses the same rows; the only difference is when rows 1 and 2 both apply (banner shows 2, push sends 1).
                                            </p>
                                        </div>
                                    )}

                                    {preview.facts && (
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Facts used</label>
                                            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                                                {Object.entries(preview.facts).map(([key, value]) => (
                                                    <div key={key} className="flex justify-between gap-2 border-b border-gray-100 py-0.5">
                                                        <dt className="text-gray-500">{key}</dt>
                                                        <dd className="text-gray-700 text-right break-all">
                                                            {value === null ? "—" : typeof value === "object" ? JSON.stringify(value) : String(value)}
                                                        </dd>
                                                    </div>
                                                ))}
                                            </dl>
                                        </div>
                                    )}
                                </>
                            )}

                            <p className="text-xs text-gray-500">
                                Rows switched on for this user: {preview.rowsOn.length ? preview.rowsOn.join(", ") : "none"}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RetentionPreviewDialog;
